import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { StateMessage } from "@/components/StateMessage";

import styles from "./MailNotice.module.css";
import type { MailDrop } from "./useMailDrop";

/**
 * What the mail's filing has to say — under the two areas, wherever on the
 * page the mail was let go.
 *
 * Under the areas, not inside the drop area: a mail dropped on „Umriss
 * zeichnen", or on the list above, never touched the drop area, and a refusal
 * has to appear next to where the person's eyes are. The list can be long
 * enough to push this off screen, so a message that arrives scrolls itself
 * into view.
 *
 * Three states, one at a time: under way (a quiet line — the call is one
 * transaction and usually quick), a refusal, and a mail that is already
 * filed, which offers that Vorgang instead of a second one.
 *
 * **The region is there before any message is** (`role="status"`, always
 * mounted). A live region announces what *changes inside it*; one that
 * arrives together with its text is not reliably read out, and after a pick
 * with the keyboard the focus stays on „Dateien auswählen" — nothing else
 * would tell the person the mail was refused. So the messages inside do not
 * announce themselves (`live={false}`), except the refusal: it interrupts
 * (`live="alert"`, the one the global constraints allow), because a mail that
 * was not filed is the answer to what the person just did.
 */
export function MailNotice({ mail }: { mail: Pick<MailDrop, "busy" | "error" | "refused" | "existing"> }) {
  const ref = useRef<HTMLDivElement>(null);
  const arrived = mail.busy || mail.error !== null || mail.existing !== null;

  useEffect(() => {
    if (arrived) ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [arrived, mail.error, mail.existing]);

  return (
    <div ref={ref} className={styles.region} role="status" aria-live="polite" data-mail-notice="">
      {mail.busy ? (
        <StateMessage tone="neutral" live={false} title="Die Mail wird abgelegt …" />
      ) : mail.existing ? (
        <StateMessage
          tone="neutral"
          strong
          live={false}
          title={`Diese Mail liegt schon in Vorgang ${mail.existing.caseNo}.`}
          action={
            <Link className="inline-link" to={`/vorgang/${mail.existing.caseId}`}>
              Vorgang öffnen
            </Link>
          }
        />
      ) : mail.error !== null ? (
        <StateMessage
          tone="conflict"
          live="alert"
          title={mail.refused ? "Nichts wurde übertragen." : "Die Mail wurde nicht abgelegt."}
        >
          {mail.error}
        </StateMessage>
      ) : null}
    </div>
  );
}
